import { useDevice } from "@decocms/blocks/sdk/useDevice";
import {
  ProductDetailsPage,
  type ImageObject,
  type VideoObject,
} from "@decocms/apps-commerce/types";
import Image from "~/components/ui/Image";
import Slider from "~/components/ui/Slider";
import ProductImageZoom from "~/components/product/ProductImageZoom";
import SliderJS from "~/components/ui/SliderJS";
import { useId, useRef, useState } from "react";
import type { MouseEvent, UIEvent } from "react";
import { getProductImageAlt } from "~/utils/product";

export interface Props {
  /** @title Integration */
  page: ProductDetailsPage | null;
}

export interface MobileProps {
  media: MediaAsset[];
  zoomId: string;
  id: string;
  productName?: string | null;
}

export interface MediaAsset {
  url: string;
  alternateName?: string;
  type: "image" | "video";
  contentUrl?: string;
  "@type"?: string;
}

const toImageMedia = (images: ImageObject[]): MediaAsset[] =>
  images
    .filter((image): image is ImageObject & { url: string } => Boolean(image.url))
    .map((image) => ({
      url: image.url,
      alternateName: image.alternateName,
      type: "image",
      "@type": image["@type"],
    }));

const toVideoMedia = (videos: VideoObject[]): MediaAsset[] =>
  videos
    .flatMap((video) => {
      const url = video.contentUrl ?? video.embedUrl;
      if (!url) {
        return [];
      }

      return [{
        url,
        alternateName: video.alternateName,
        type: "video" as const,
        contentUrl: video.contentUrl,
        "@type": video["@type"],
      }];
    });

const MOBILE_WIDTH = 358;
const MOBILE_HEIGHT = 528.3;
const DESKTOP_WIDTH = 556;
const DESKTOP_HEIGHT = 815;
const ASPECT_RATIO = (WIDTH: number, HEIGHT: number) => `${WIDTH} / ${HEIGHT}`;

/**
 * @title Product Image Slider
 * @description Creates a three columned grid on destkop, one for the dots preview, one for the image slider and the other for product info
 * On mobile, there's one single column with 3 rows. Note that the orders are different from desktop to mobile, that's why
 * we rearrange each cell with col-start- directives
 */

// Estado de zoom da galeria: qual imagem está com zoom ativo e a partir de
// que ponto (transform-origin) o zoom deve ser centralizado. Substitui a
// antiga manipulação direta do DOM (classList + querySelectorAll) por
// estado React equivalente. Como cada imagem é renderizada duas vezes
// (variante mobile e variante desktop, alternadas via CSS), um único índice
// compartilhado reproduz fielmente o comportamento anterior de "só uma
// imagem com zoom por vez".
type ImageZoomHandlers = {
  zoomedIndex: number | null;
  zoomOrigin: string;
  onMobileClick: (index: number, e: MouseEvent<HTMLImageElement>) => void;
  onDesktopEnter: (index: number) => void;
  onDesktopLeave: (index: number) => void;
  onDesktopMove: (index: number, e: MouseEvent<HTMLImageElement>) => void;
};

function useImageZoom() {
  const [zoomedIndex, setZoomedIndex] = useState<number | null>(null);
  const [zoomOrigin, setZoomOrigin] = useState("center");

  const originFromEvent = (e: MouseEvent<HTMLImageElement>) => {
    const imageRect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - imageRect.left;
    const y = e.clientY - imageRect.top;

    return `${(x / imageRect.width) * 100}% ${
      (y / imageRect.height) * 100
    }%`;
  };

  // Zoom via clique (mobile): alterna a imagem clicada e desativa
  // automaticamente qualquer outra que estivesse com zoom ativo.
  const onMobileClick = (index: number, e: MouseEvent<HTMLImageElement>) => {
    setZoomOrigin(originFromEvent(e));
    setZoomedIndex((current) => (current === index ? null : index));
  };

  // Zoom via hover (desktop)
  const onDesktopEnter = (index: number) => setZoomedIndex(index);

  const onDesktopLeave = (index: number) =>
    setZoomedIndex((current) => (current === index ? null : current));

  const onDesktopMove = (index: number, e: MouseEvent<HTMLImageElement>) => {
    setZoomOrigin(originFromEvent(e));
  };

  return {
    zoomedIndex,
    zoomOrigin,
    setZoomedIndex,
    onMobileClick,
    onDesktopEnter,
    onDesktopLeave,
    onDesktopMove,
  };
}

// Função para renderizar tanto imagens quanto vídeos com as dimensões adaptativas - SSR friendly
const renderMedia = (
  mediaItem: MediaAsset,
  index: number,
  width: number,
  height: number,
  _totalMedia: number,
  productName: string | null | undefined,
  zoom: ImageZoomHandlers,
) => {
  // Renderiza vídeos com controles automáticos e autoplay otimizado - SSR friendly
  if (mediaItem["@type"] === "VideoObject" || mediaItem.type === "video") {
    return (
      <>
        {/* Mobile Video */}
        <video
          className={`productVideo object-contain aspect-[375/400] w-[375px] h-[400px] lg:hidden`}
          src={mediaItem.contentUrl || mediaItem.url}
          width={375}
          height={400}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          controls={false}
        />
        {/* Desktop Video */}
        <video
          className="productVideo cursor-pointer hidden lg:block"
          style={{
            aspectRatio: ASPECT_RATIO(width, height),
            objectFit: "contain",
            width: width,
            height: height,
          }}
          src={mediaItem.contentUrl || mediaItem.url}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          controls={false}
        />
      </>
    );
  }

  // Renderiza imagens com zoom interativo - SSR friendly (renderiza ambos mobile e desktop)
  const imageAlt = getProductImageAlt(productName, index);
  const isZoomed = zoom.zoomedIndex === index;

  return (
    <>
      {/* Mobile Image */}
      <Image
        className={`productImages cursor-zoom-in object-contain aspect-[375/400] w-[375px] h-[400px] lg:hidden${
          isZoomed ? " zoom" : ""
        }`}
        style={isZoomed ? { transformOrigin: zoom.zoomOrigin } : undefined}
        sizes="375px"
        src={mediaItem.url}
        alt={`${imageAlt} - Mobile`}
        width={375}
        height={400}
        preload={index === 0}
        loading={index === 0 ? "eager" : "lazy"}
        fetchPriority={index === 0 ? "high" : "low"}
        decoding="async"
        onClick={(e) => zoom.onMobileClick(index, e)}
      />
      {/* Desktop Image */}
      <Image
        className={`productImages cursor-zoom-in hidden lg:block${
          isZoomed ? " zoom" : ""
        }`}
        sizes={`(max-width: 640px) 100vw, ${width * 4}`}
        style={{
          aspectRatio: ASPECT_RATIO(width, height),
          objectFit: "contain",
          width: width,
          height: height,
          ...(isZoomed ? { transformOrigin: zoom.zoomOrigin } : {}),
        }}
        src={mediaItem.url}
        alt={`${imageAlt} - Desktop`}
        width={width}
        height={height}
        preload={index === 0}
        loading={index === 0 ? "eager" : "lazy"}
        fetchPriority={index === 0 ? "high" : "low"}
        decoding="async"
        onMouseEnter={() => zoom.onDesktopEnter(index)}
        onMouseLeave={() => zoom.onDesktopLeave(index)}
        onMouseMove={(e) => zoom.onDesktopMove(index, e)}
      />
    </>
  );
};

function Dots(
  { media, onDotClick }: { media: MediaAsset[]; onDotClick: () => void },
) {
  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @property --dot-progress {
            syntax: '<percentage>';
            inherits: false;
            initial-value: 0%;
          }
          `,
        }}
      />
      <ul className="carousel justify-center col-span-full gap-2 z-10 absolute bottom-12 w-full px-4">
        {media?.map((_, index) => (
          <li className="carousel-item">
            <Slider.Dot index={index}>
              <div
                className="w-3 h-3 rounded-full border-2 border-[#333333] bg-[#F0F0F0] group-disabled:bg-[#1A1A1A] group-disabled:border-[#1A1A1A] transition-colors duration-300"
                onClick={onDotClick}
              >
              </div>
            </Slider.Dot>
          </li>
        ))}
      </ul>
    </>
  );
}

const Mobile = ({ media, id, productName }: MobileProps) => {
  const {
    zoomedIndex,
    zoomOrigin,
    setZoomedIndex,
    onMobileClick,
    onDesktopEnter,
    onDesktopLeave,
    onDesktopMove,
  } = useImageZoom();
  const zoom: ImageZoomHandlers = {
    zoomedIndex,
    zoomOrigin,
    onMobileClick,
    onDesktopEnter,
    onDesktopLeave,
    onDesktopMove,
  };

  // Guarda o scrollLeft do slider entre eventos de scroll para detectar
  // mudança de slide e desativar o zoom ativo, substituindo o antigo
  // data-attribute lido/escrito direto no DOM.
  const lastScrollLeftRef = useRef(0);

  const onSliderScroll = (e: UIEvent<HTMLUListElement>) => {
    const slider = e.currentTarget;
    const lastScrollLeft = lastScrollLeftRef.current;
    const currentScrollLeft = slider.scrollLeft;

    if (
      lastScrollLeft !== 0 &&
      Math.abs(currentScrollLeft - lastScrollLeft) > 10
    ) {
      // Slide mudou significativamente - desativa zoom da imagem ativa
      setZoomedIndex(null);
    }

    lastScrollLeftRef.current = currentScrollLeft;
  };

  // Desativa zoom quando clica nos dots
  const onDotClick = () => {
    setTimeout(() => setZoomedIndex(null), 100);
  };

  return (
    <div
      id={id}
      className="grid grid-flow-row sm:grid-flow-col grid-cols-1 sm:grid-cols-[min-content_1fr] gap-5 relative"
    >
      {/* Media Slider */}
      <div className="col-start-1 col-span-1 sm:col-start-2">
        <div className="relative h-min grow flex items-center justify-center">
          <Slider
            className="carousel carousel-center gap-6 w-full"
            onScroll={onSliderScroll}
          >
            {media.map((mediaItem, index) => (
              <Slider.Item
                index={index}
                className="carousel-item w-full justify-center items-center flex"
              >
                {renderMedia(
                  mediaItem,
                  index,
                  MOBILE_WIDTH,
                  MOBILE_HEIGHT,
                  media.length,
                  productName,
                  zoom,
                )}
              </Slider.Item>
            ))}
          </Slider>
        </div>
      </div>

      {/* Dots */}
      <Dots media={media} onDotClick={onDotClick} />

      <SliderJS rootId={id} />
    </div>
  );
};

/**
 * Função para determinar dimensões baseada na quantidade de mídia (imagens + vídeos) e posição
 *
 * Layout visual esperado:
 *
 * 1 MÍDIA:
 * [    706x966    ]
 *
 * 2 MÍDIAS:
 * [    706x966    ]
 * [    706x966    ]
 *
 * 5 MÍDIAS:
 * [ 345x472 ][ 345x472 ]
 * [ 345x472 ][ 345x472 ]
 * [      706x966        ]
 *
 * 6 MÍDIAS:
 * [ 345x472 ][ 345x472 ]
 * [ 345x472 ][ 345x472 ]
 * [ 345x472 ][ 345x472 ]
 */
const getMediaDimensions = (
  totalMedia: number,
  index: number,
): [number, number] => {
  switch (totalMedia) {
    case 1:
      return [836, 1116]; // 1 mídia: 836px x 1116px centralizada

    case 2:
      if (index === 0) {
        return [836, 1116]; // primeira mídia: 836px x 1116px
      }
      return [836, 996]; // segunda mídia: 836px x 996px

    case 5:
      if (index === 4) { // última mídia (índice 4)
        return [836, 966]; // quinta mídia maior, ocupa toda a largura inferior
      }
      return [410, 550]; // primeiras 4 mídias menores em grid 2x2

    case 6:
      return [410, 550]; // todas as 6 mídias: 410px x 550px em grid 3x2

    default:
      // Para outros números de mídias, usar tamanho padrão
      return [410, 550];
  }
};

// Função para determinar o layout CSS baseado na quantidade de mídia (imagens + vídeos)
const getGridClass = (totalMedia: number): string => {
  switch (totalMedia) {
    case 1:
      return "flex justify-center"; // 1 mídia centralizada

    case 2:
      return "flex flex-col gap-6 items-center"; // 2 mídias uma em cima da outra, centralizadas

    case 5:
      return "grid grid-cols-2 gap-3 auto-rows-min"; // Grid 2x2 + 1 grande (última linha ocupa 2 colunas)

    case 6:
      return "grid grid-cols-2 gap-3"; // Grid 2x3 (2 colunas, 3 linhas)

    default:
      return "grid grid-cols-2 gap-3"; // Layout padrão
  }
};

const Desktop = (
  { media, productName }: { media: MediaAsset[]; productName?: string | null },
) => {
  const totalMedia = media.length;
  const {
    zoomedIndex,
    zoomOrigin,
    onMobileClick,
    onDesktopEnter,
    onDesktopLeave,
    onDesktopMove,
  } = useImageZoom();
  const zoom: ImageZoomHandlers = {
    zoomedIndex,
    zoomOrigin,
    onMobileClick,
    onDesktopEnter,
    onDesktopLeave,
    onDesktopMove,
  };

  return (
    <div className={getGridClass(totalMedia)}>
      {media.map((mediaItem, index) => {
        const [width, height] = getMediaDimensions(totalMedia, index);

        // Para layout de 5 mídias, a última (índice 4) ocupa 2 colunas e é centralizada
        const gridSpanClass = totalMedia === 5 && index === 4
          ? "col-span-2 flex justify-center"
          : "";

        return (
          <div key={index} className={`overflow-hidden ${gridSpanClass}`}>
            {renderMedia(
              mediaItem,
              index,
              width,
              height,
              totalMedia,
              productName,
              zoom,
            )}
          </div>
        );
      })}
    </div>
  );
};

// Loading component
const MediaLoading = () => (
  <div className="flex justify-center items-center min-h-[400px] lg:min-h-[600px]">
    <div className="animate-pulse flex space-x-4">
      <div className="bg-gray-300 rounded h-32 w-24"></div>
      <div className="bg-gray-300 rounded h-32 w-24"></div>
      <div className="bg-gray-300 rounded h-32 w-24"></div>
    </div>
  </div>
);

export default function GallerySlider(props: Props) {
  const id = useId();
  const zoomId = `${id}-zoom`;
  const device = useDevice();

  if (!props.page) {
    throw new Error("Missing Product Details Page Info");
  }

  const {
    page: {
      product: { name, isVariantOf, image: pImages, video: pVideos },
    },
  } = props;

  // Get product name for alt text
  const productName = isVariantOf?.name || name;

  // Get images and videos from product data - combinar todas as fontes disponíveis
  const productImages = Array.isArray(pImages) ? pImages : [];
  const variantImages = Array.isArray(isVariantOf?.image)
    ? isVariantOf.image
    : [];
  // Usar a fonte com mais imagens, ou combinar se necessário
  const images = variantImages.length >= productImages.length
    ? variantImages
    : productImages;

  const productVideos = Array.isArray(pVideos) ? pVideos : [];
  const variantVideos = Array.isArray(isVariantOf?.video)
    ? isVariantOf.video
    : [];
  // Usar a fonte com mais vídeos, ou combinar se necessário
  const videos = variantVideos.length >= productVideos.length
    ? variantVideos
    : productVideos;

  // Add type property to images to distinguish them from videos
  const imageMedia = toImageMedia(images);
  const videoMedia = toVideoMedia(videos);

  // Combine media (images first, then videos) with a normalized shape
  const media: MediaAsset[] = [...imageMedia, ...videoMedia];

  // Validação de mídia antes de renderizar
  const isMediaReady = media.length > 0 &&
    media.every((item) =>
      (item.url || item.contentUrl) &&
      (item.type === "image" || item.type === "video")
    );

  // Filter only images for the zoom component (ProductImageZoom doesn't support videos)
  const onlyImages = media.filter((item) => item.type === "image");

  // Show loading while media is not ready
  if (!isMediaReady) {
    return <MediaLoading />;
  }

  return (
    <>
      {device === "mobile"
        ? (
          <Mobile
            media={media}
            zoomId={zoomId}
            id={id}
            productName={productName}
          />
        )
        : <Desktop media={media} productName={productName} />}
      {onlyImages.length > 0 && (
        <ProductImageZoom
          images={onlyImages}
          width={700}
          height={Math.trunc((700 * DESKTOP_HEIGHT) / DESKTOP_WIDTH)}
          productName={productName}
        />
      )}
    </>
  );
}
